import { motion } from 'framer-motion';
import { forwardRef, HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { fadeInUp } from '@/lib/motion';

interface GlassPanelProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  blur?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'subtle' | 'prominent';
  animate?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
}

const blurMap = {
  sm: 'backdrop-blur-sm',
  md: 'backdrop-blur-md',
  lg: 'backdrop-blur-lg',
  xl: 'backdrop-blur-xl',
};

const variantStyles = {
  default: 'bg-white/[0.03] border-white/[0.08]',
  subtle: 'bg-white/[0.02] border-white/[0.05]',
  prominent: 'bg-white/[0.06] border-white/[0.12]',
};

const paddingStyles = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
  xl: 'p-10',
};

/**
 * GlassPanel - Glassmorphism container component
 * 
 * Features:
 * - Frosted glass effect with backdrop blur
 * - Subtle border glow
 * - Optional entrance animation
 * - Multiple variants and sizes
 */
export const GlassPanel = forwardRef<HTMLDivElement, GlassPanelProps>(
  (
    { 
      children, 
      className, 
      blur = 'lg', 
      variant = 'default',
      animate = true,
      padding = 'md',
      ...props 
    }, 
    ref
  ) => {
    const baseClassName = cn(
      // Base glass styles
      'relative rounded-2xl border',
      blurMap[blur],
      variantStyles[variant],
      paddingStyles[padding],
      // Hover effect
      'transition-all duration-300',
      'hover:bg-white/[0.05] hover:border-white/[0.12]',
      // Shadow
      'shadow-glass',
      className
    );

    if (!animate) {
      return (
        <div ref={ref} className={baseClassName} {...props}>
          {/* Inner glow effect */}
          <div 
            className="absolute inset-0 rounded-2xl opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse at 50% 0%, rgba(6,182,212,0.05) 0%, transparent 60%)',
            }}
          />
          {/* Content */}
          <div className="relative z-10">
            {children}
          </div>
        </div>
      );
    }

    return (
      <motion.div
        ref={ref}
        className={baseClassName}
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={fadeInUp}
      >
        {/* Inner glow effect */}
        <div 
          className="absolute inset-0 rounded-2xl opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 50% 0%, rgba(6,182,212,0.05) 0%, transparent 60%)',
          }}
        />
        
        {/* Content */}
        <div className="relative z-10">
          {children}
        </div>
      </motion.div>
    );
  }
);

GlassPanel.displayName = 'GlassPanel';

export default GlassPanel;

